/**
 * API client for RideSpot backend.
 * In local development, default to the local API to avoid accidentally talking to production.
 */
const DEFAULT_API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:3001/api'
    : 'https://api.ride-spot.com/api';
const BASE = process.env.REACT_APP_API_URL || DEFAULT_API_BASE;
const TOKEN_KEY = 'ridespot_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function hasToken(): boolean {
  return !!getToken();
}

async function request<T>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth, ...init } = options;
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (!(init.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token && !skipAuth) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data as T;
}

// Auth
export const authApi = {
  register: (username: string, displayName: string, email: string, password: string, recaptchaToken?: string) =>
    request<{ success: true; message?: string }>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify(
          recaptchaToken ? { username, displayName, email, password, recaptchaToken } : { username, displayName, email, password }
        ),
        skipAuth: true,
      }
    ),
  resendVerificationEmail: (email: string) =>
    request<{ success: boolean; message?: string }>(
      '/auth/resend-verification',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
        skipAuth: true,
      }
    ),
  login: (identifier: string, password: string, recaptchaToken?: string) =>
    request<{ success: true; user: { id: string; username: string; displayName: string }; token: string }>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(
          recaptchaToken ? { identifier, password, recaptchaToken } : { identifier, password }
        ),
        skipAuth: true,
      }
    ),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request<{ id: string; username: string; displayName: string; email?: string; avatarUrl?: string; bio?: string; location?: string }>('/auth/me'),
  demoLogin: (userId: string) =>
    request<{ success: true; user: { id: string; username: string; displayName: string }; token: string }>(
      '/auth/demo',
      { method: 'POST', body: JSON.stringify({ userId }), skipAuth: true }
    ),
};

// Users
export interface TopContributorApi {
  user: { id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string };
  likedCount: number;
  beautifulCount: number;
  coolCount: number;
  sweetPhotoCount: number;
  spotCount: number;
  contributorScore: number;
}

export const usersApi = {
  list: () => request<Array<{ id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string }>>('/users'),
  get: (id: string) => request<{ id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string }>(`/users/${id}`),
  getByUsername: (username: string) =>
    request<{ id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string }>(
      `/users/by-username/${encodeURIComponent(username)}`
    ),
  updateMe: (body: { username?: string; displayName?: string; email?: string; avatarUrl?: string | null }) =>
    request<{ id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string }>('/users/me', { method: 'PATCH', body: JSON.stringify(body) }),
  getMyCollections: () =>
    request<{ liked: SpotApi[]; beautiful: SpotApi[]; cool: SpotApi[]; sweetPhoto: SpotApi[]; tagged: SpotApi[] }>('/users/me/collections'),
  topContributors: () =>
    request<{ contributors: TopContributorApi[] }>('/users/top-contributors'),
  topSpottersWeek: () =>
    request<{ contributors: TopContributorApi[] }>('/users/top-spotters-week'),
};

// Badges
export interface BadgeApi {
  id: string;
  name: string;
  requirement: number;
  tier: 'green' | 'blue' | 'purple';
  rank: number;
  category: 'spotter' | 'garage';
}

export const badgesApi = {
  getDefinitions: () =>
    request<{ spotter: BadgeApi[]; garage: BadgeApi[] }>('/badges/definitions'),
  getForUser: (userId: string) =>
    request<{
      spotCount: number;
      garageCount: number;
      earnedBadges: BadgeApi[];
      topBadges: BadgeApi[];
    }>(`/badges/user/${userId}`),
};

// Contact form (public; no auth header so invalid tokens do not affect submission)
export const contactApi = {
  submit: (body: { topic: string; email: string; details: string; recaptchaToken?: string }) =>
    request<{ success: true; message?: string }>('/feedback', {
      method: 'POST',
      body: JSON.stringify(body),
      skipAuth: true,
    }),
};

// Spots
export interface CarMakeApi {
  id: string;
  name: string;
}

export interface SpotApi {
  id: string;
  authorId: string;
  author?: { id: string; username: string; displayName: string; topBadges?: BadgeApi[] };
  imageUrl: string;
  images: string[];
  caption?: string;
  car: Record<string, string | undefined>;
  location?: string;
  city?: string;
  countryCode?: string;
  event?: string;
  taggedOwnerId?: string;
  taggedOwner?: { id: string; username: string; displayName: string };
  likes: string[];
  beautiful?: string[];
  cool?: string[];
  sweetPhoto?: string[];
  comments: Array<{
    id: string;
    authorId: string;
    author?: { id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string };
    parentCommentId?: string;
    replyToCommentId?: string;
    body: string;
    createdAt: string;
  }>;
  likeCount?: number;
  beautifulCount?: number;
  coolCount?: number;
  sweetPhotoCount?: number;
  commentCount?: number;
  likedByCurrentUser?: boolean;
  beautifulByCurrentUser?: boolean;
  coolByCurrentUser?: boolean;
  sweetPhotoByCurrentUser?: boolean;
  isSummary?: boolean;
  createdAt: string;
  updatedAt?: string;
  viewCount?: number;
}

export interface SpotFeedStatsApi {
  totalCount: number;
  uniqueSpotterCount: number;
  countryCount: number;
}

export interface SpotFeedPageApi {
  items: SpotApi[];
  nextCursor: string | null;
  hasMore: boolean;
  stats?: SpotFeedStatsApi;
}

export const spotsApi = {
  list: () => request<SpotApi[]>('/spots'),
  feed: (params: { limit?: number; cursor?: string | null; authorId?: string; includeStats?: boolean } = {}) => {
    const sp = new URLSearchParams();
    if (params.limit != null) sp.set('limit', String(params.limit));
    if (params.cursor) sp.set('cursor', params.cursor);
    if (params.authorId) sp.set('authorId', params.authorId);
    if (params.includeStats) sp.set('includeStats', '1');
    const query = sp.toString();
    return request<SpotFeedPageApi>(`/spots/feed${query ? `?${query}` : ''}`);
  },
  tagged: () => request<SpotApi[]>('/spots/tagged'),
  carMakes: () => request<CarMakeApi[]>('/spots/car-makes', { skipAuth: true }),
  get: (id: string, options?: { incrementView?: boolean }) => {
    const sp = new URLSearchParams();
    if (options?.incrementView === false) sp.set('incrementView', '0');
    const query = sp.toString();
    return request<SpotApi>(`/spots/${id}${query ? `?${query}` : ''}`);
  },
  search: (make: string, model: string) => {
    const params = new URLSearchParams();
    if (make.trim()) params.set('make', make.trim());
    if (model.trim()) params.set('model', model.trim());
    return request<SpotApi[]>(`/spots/search?${params.toString()}`);
  },
  mostVoted: () =>
    request<{ liked: SpotApi[]; beautiful: SpotApi[]; cool: SpotApi[]; sweetPhoto: SpotApi[] }>('/spots/most-voted'),
  create: (body: {
    images: Array<{ filename: string; imageHash?: string }>;
    caption?: string;
    car: Record<string, string | undefined>;
    location?: string;
    city?: string;
    countryCode?: string;
    event?: string;
    taggedOwnerId?: string;
  }) => request<SpotApi>('/spots', { method: 'POST', body: JSON.stringify(body) }),
  like: (id: string) => request<SpotApi>(`/spots/${id}/like`, { method: 'POST' }),
  unlike: (id: string) => request<SpotApi>(`/spots/${id}/like`, { method: 'DELETE' }),
  beautiful: (id: string) => request<SpotApi>(`/spots/${id}/beautiful`, { method: 'POST' }),
  unBeautiful: (id: string) => request<SpotApi>(`/spots/${id}/beautiful`, { method: 'DELETE' }),
  cool: (id: string) => request<SpotApi>(`/spots/${id}/cool`, { method: 'POST' }),
  unCool: (id: string) => request<SpotApi>(`/spots/${id}/cool`, { method: 'DELETE' }),
  sweetPhoto: (id: string) => request<SpotApi>(`/spots/${id}/sweet-photo`, { method: 'POST' }),
  unSweetPhoto: (id: string) => request<SpotApi>(`/spots/${id}/sweet-photo`, { method: 'DELETE' }),
  addComment: (id: string, body: string, replyToCommentId?: string) =>
    request<SpotApi>(`/spots/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(replyToCommentId ? { body, replyToCommentId } : { body }),
    }),
  update: (id: string, body: Record<string, unknown>) => request<SpotApi>(`/spots/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => request<void>(`/spots/${id}`, { method: 'DELETE' }),
};

/**
 * Browser URL for a stored upload reference.
 * Relative paths (e.g. spots/photo.jpg) become {origin}/uploads/{path}.
 * Already-absolute URLs (e.g. Azure blob URLs from the upload API) are returned unchanged.
 */
export function uploadUrl(storedPath: string): string {
  if (!storedPath) return '';
  const s = storedPath.trim();
  if (/^https?:\/\//i.test(s)) return s;
  const uploadsBase = BASE.replace(/\/api\/?$/, '').replace(/\/$/, '');
  return `${uploadsBase}/uploads/${s}`;
}

export interface UploadIdentifiedCarApi {
  make: string;
  model: string;
  trim?: string;
  generation?: string;
  year?: number;
  yearRange?: string;
  bodyStyle?: string;
  performanceType?: string;
  confidence: number;
}

export const uploadApi = {
  spot: (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('images', f));
    return request<{ files: Array<{ filename: string; imageHash: string }>; identifiedCar?: UploadIdentifiedCarApi }>('/upload/spot', { method: 'POST', body: form });
  },
  identifySpot: (file: File) => {
    const form = new FormData();
    form.append('image', file);
    return request<{ identifiedCar?: UploadIdentifiedCarApi }>('/upload/spot/identify', { method: 'POST', body: form });
  },
  garage: (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('images', f));
    return request<{ filenames: string[] }>('/upload/garage', { method: 'POST', body: form });
  },
  profile: (file: File) => {
    const form = new FormData();
    form.append('image', file);
    return request<{ filename: string }>('/upload/profile', { method: 'POST', body: form });
  },
};

// Garage
export interface GarageCarImageApi {
  url: string;
  caption?: string;
}

export interface GarageModApi {
  id: string;
  garageCarId: string;
  category: string;
  title: string;
  details?: string;
  dateCompleted?: string;
  images: { url: string; caption?: string }[];
  createdAt: string;
  updatedAt?: string;
}

export interface TrackSessionApi {
  id: string;
  garageCarId: string;
  venueName: string;
  sessionDate: string;
  eventName?: string;
  eventType: string;
  bestLapTimeSeconds: number | null;
  bestLapTimeDisplay: string;
  slowestLapTimeSeconds: number | null;
  slowestLapTimeDisplay: string;
  lapCount: number | null;
  tiresUsed?: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface MaintenanceRecordApi {
  id: string;
  garageCarId: string;
  serviceType: string;
  title: string;
  performedOn: string;
  performedOdometer?: number | null;
  notes?: string;
  cost?: number | null;
  shopName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface MaintenanceScheduleItemApi {
  serviceType: string;
  title: string;
  description: string;
  intervalMiles?: number;
  intervalMonths?: number;
  dueDate?: string;
  predictedDueDate?: string;
  dueOdometer?: number;
  remainingMiles?: number;
  status: 'upcoming' | 'due_soon' | 'overdue';
  dueReason: 'time' | 'mileage' | 'time_and_mileage' | 'inspection';
  lastCompletedOn?: string;
  lastCompletedOdometer?: number | null;
}

export interface GarageCarApi {
  id: string;
  ownerId: string;
  name?: string;
  car: { make: string; model: string; year?: number; bodyStyle?: string; powertrainType?: string; performanceType?: string; trim?: string };
  year?: number;
  trim?: string;
  engine?: string;
  mods?: string[];
  garageMods?: GarageModApi[];
  trackSessions?: TrackSessionApi[];
  performanceSpecs?: Record<string, string>;
  colorCode?: string;
  colorName?: string;
  vin?: string;
  currentOdometer?: number | null;
  averageMilesPerMonth?: number | null;
  maintenanceRemindersEnabled?: boolean;
  lastOdometerUpdatedAt?: string;
  images: GarageCarImageApi[];
  photos: string[];
  buildProgressPhotos?: Array<{ url: string; caption?: string; date?: string }>;
  isPastCar?: boolean;
  acquiredDate?: string;
  soldDate?: string;
  isForSale?: boolean;
  saleSellingDetails?: string;
  saleCarCondition?: string;
  saleCarfaxLink?: string;
  saleCarFeatures?: string;
  salePrice?: string;
  saleMilesDriven?: number | null;
  isPrimary?: boolean;
  likes: string[];
  comments: Array<{ id: string; authorId: string; body: string; createdAt: string }>;
  maintenanceRecords?: MaintenanceRecordApi[];
  maintenanceSchedule?: MaintenanceScheduleItemApi[];
  createdAt: string;
  updatedAt?: string;
}

export const garageApi = {
  list: () => request<GarageCarApi[]>('/garage'),
  get: (id: string) => request<GarageCarApi>(`/garage/${id}`),
  create: (body: Record<string, unknown>) => request<GarageCarApi>('/garage', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: Record<string, unknown>) => request<GarageCarApi>(`/garage/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (id: string) => request(`/garage/${id}`, { method: 'DELETE' }),
  getMod: (modId: string) => request<GarageModApi>(`/garage/mods/${modId}`),
  createMod: (carId: string, body: Record<string, unknown>) =>
    request<GarageModApi>(`/garage/${carId}/mods`, { method: 'POST', body: JSON.stringify(body) }),
  updateMod: (modId: string, body: Record<string, unknown>) =>
    request<GarageModApi>(`/garage/mods/${modId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteMod: (modId: string) => request(`/garage/mods/${modId}`, { method: 'DELETE' }),
  getSession: (sessionId: string) => request<TrackSessionApi>(`/garage/sessions/${sessionId}`),
  createSession: (carId: string, body: Record<string, unknown>) =>
    request<TrackSessionApi>(`/garage/${carId}/sessions`, { method: 'POST', body: JSON.stringify(body) }),
  updateSession: (sessionId: string, body: Record<string, unknown>) =>
    request<TrackSessionApi>(`/garage/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteSession: (sessionId: string) => request(`/garage/sessions/${sessionId}`, { method: 'DELETE' }),
  createMaintenanceRecord: (carId: string, body: Record<string, unknown>) =>
    request<MaintenanceRecordApi>(`/garage/${carId}/maintenance-records`, { method: 'POST', body: JSON.stringify(body) }),
  updateMaintenanceRecord: (recordId: string, body: Record<string, unknown>) =>
    request<MaintenanceRecordApi>(`/garage/maintenance-records/${recordId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteMaintenanceRecord: (recordId: string) =>
    request(`/garage/maintenance-records/${recordId}`, { method: 'DELETE' }),
};

// Garages (public: featured lists + view user garage)
export interface FeaturedGarageEntry {
  user: { id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string };
  engagementScore?: number;
  popularityScore?: number;
  commentCount?: number;
  spotCount?: number;
  taggedSpotCount?: number;
  completenessScore?: number;
  garageCarCount: number;
  previewPhotoUrl?: string;
}

export interface FeaturedGaragesApi {
  byEngagement: FeaturedGarageEntry[];
  byPopularity: FeaturedGarageEntry[];
  byCompleteness: FeaturedGarageEntry[];
}

export interface GarageSearchEntry {
  user: { id: string; username: string; displayName: string; avatarUrl?: string; bio?: string; location?: string };
  garageCarCount: number;
  previewPhotoUrl?: string;
}

export interface GarageSearchApi {
  items: GarageSearchEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const garagesApi = {
  featured: () => request<FeaturedGaragesApi>('/garages/featured'),
  search: (params: { page?: number; limit?: number; displayName?: string; make?: string; model?: string }) => {
    const sp = new URLSearchParams();
    if (params.page != null) sp.set('page', String(params.page));
    if (params.limit != null) sp.set('limit', String(params.limit));
    if (params.displayName) sp.set('displayName', params.displayName);
    if (params.make) sp.set('make', params.make);
    if (params.model) sp.set('model', params.model);
    const q = sp.toString();
    return request<GarageSearchApi>(`/garages/search${q ? `?${q}` : ''}`);
  },
  getByUser: (userId: string) => request<GarageCarApi[]>(`/garages/user/${userId}`),
  getCar: (id: string) => request<GarageCarApi>(`/garages/car/${id}`),
  likeCar: (id: string) => request<GarageCarApi>(`/garages/car/${id}/like`, { method: 'POST' }),
  unlikeCar: (id: string) => request<GarageCarApi>(`/garages/car/${id}/like`, { method: 'DELETE' }),
  addCarComment: (id: string, body: string) =>
    request<GarageCarApi>(`/garages/car/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  getMod: (modId: string) => request<GarageModApi>(`/garages/mods/${modId}`),
};

// Favorites
export interface FavoriteListApi {
  id: string;
  userId: string;
  name: string;
  spotIds: string[];
  createdAt: string;
}

export const favoritesApi = {
  list: () => request<FavoriteListApi[]>('/favorites'),
  create: (name: string) => request<FavoriteListApi>('/favorites', { method: 'POST', body: JSON.stringify({ name }) }),
  addSpot: (listId: string, spotId: string) =>
    request<FavoriteListApi>(`/favorites/${listId}/spots/${spotId}`, { method: 'POST' }),
  removeSpot: (listId: string, spotId: string) =>
    request<FavoriteListApi>(`/favorites/${listId}/spots/${spotId}`, { method: 'DELETE' }),
  getSpots: (listId: string) => request<SpotApi[]>(`/favorites/${listId}/spots`),
};
